/**
 * My Courses — which course families are shown, in what order.
 *
 * Pure, and kept out of the page because it is the part with rules: a course
 * family (a root plus its translations) is ONE row here, so "is it published",
 * "when was it last edited" and "what language is it" are all questions about
 * the family rather than about one course.
 */
import type { CourseGroup } from '@/lib/db'

/** 'launched' (S4): families with a launch in the organisation — Owner/Admins only. */
export type CourseStatusFilter = 'all' | 'draft' | 'published' | 'launched'
/** 'needs' (12 Oct 2026, the shell's Courses page): what needs you first — see courseEntries. Over
 *  families alone it is Last edited. */
export type CourseSort = 'needs' | 'edited' | 'created' | 'az' | 'za'

export const COURSE_SORTS: { id: CourseSort; label: string }[] = [
  { id: 'edited', label: 'Last edited' },
  { id: 'created', label: 'Newest first' },
  { id: 'az', label: 'Title A–Z' },
  { id: 'za', label: 'Title Z–A' },
]
/** The app shell's Courses page adds “Needs you first” (its default); the older page keeps the four. */
export const SHELL_SORTS: { id: CourseSort; label: string }[] = [{ id: 'needs', label: 'Needs you first' }, ...COURSE_SORTS]

/**
 * A family counts as published when ANY of its languages has a share code.
 * The alternative — only the original counts — would file a course whose
 * Hindi version is live with the drafts, which is the opposite of what the
 * creator is looking for when they filter.
 */
export function isPublished(group: CourseGroup): boolean {
  return group.all.some(c => !!c.share_code)
}

const time = (iso: string | undefined | null) => (iso ? Date.parse(iso) || 0 : 0)

/** The most recent edit anywhere in the family. */
export function lastEdited(group: CourseGroup): number {
  return Math.max(...group.all.map(c => time(c.updated_at)))
}

/** When the original was created — a translation made later does not make the course newer. */
export function createdAt(group: CourseGroup): number {
  return time(group.root.created_at)
}

const title = (group: CourseGroup) => (group.root.title || 'Untitled Course').trim().toLowerCase()

/** Language codes present across every family, for the language filter. */
export function languagesIn(groups: CourseGroup[]): string[] {
  return [...new Set(groups.flatMap(g => g.all.map(c => c.language).filter(Boolean)))].sort()
}

export function statusCounts(groups: CourseGroup[], launched?: ReadonlySet<string>): { all: number; published: number; draft: number; launched: number } {
  const published = groups.filter(isPublished).length
  return { all: groups.length, published, draft: groups.length - published, launched: launched ? groups.filter(g => launched.has(g.root.id)).length : 0 }
}

export interface CourseListOptions {
  /**
   * Free text, matched against every language version's title. Search is
   * GLOBAL: while there is a query the status tab and the language filter are
   * ignored, because a creator searching for a course they half-remember does
   * not know, and should not have to guess, which tab it is filed under.
   */
  query?: string
  status?: CourseStatusFilter
  /** A language code, or 'all'. Matches a family with a version in it. */
  language?: string
  /**
   * Several languages at once (the Filters panel) — a family matches if it has
   * a version in ANY of them. MULTI_LANGUAGE matches a family with more than one
   * language. Empty = no language filter. Used instead of `language` when set.
   */
  languages?: string[]
  /** Families whose course was created by any of these users. Empty = everyone. */
  authors?: string[]
  sort?: CourseSort
  /** Root ids of launched families, for the Launched tab. */
  launched?: ReadonlySet<string>
}

/** The "Multiple languages" choice in the Filters panel. */
export const MULTI_LANGUAGE = '__multi'

export function familyLanguages(g: CourseGroup): string[] {
  return [...new Set(g.all.map(c => c.language).filter(Boolean))] as string[]
}

function matchesLanguages(g: CourseGroup, wanted: string[]): boolean {
  const langs = familyLanguages(g)
  return wanted.some(w => (w === MULTI_LANGUAGE ? langs.length > 1 : langs.includes(w)))
}

/** Authors present across the families (root course creator), for the Author filter. */
export function authorsIn(groups: CourseGroup[]): string[] {
  return [...new Set(groups.map(g => g.root.created_by).filter(Boolean))]
}

/** Filter and sort the families. Never mutates its input. */
export function courseListView(groups: CourseGroup[], opts: CourseListOptions = {}): CourseGroup[] {
  const { query = '', status = 'all', language = 'all', languages = [], authors = [], sort = 'edited', launched } = opts
  const q = query.trim().toLowerCase()

  const kept = groups.filter(g => {
    if (q) return g.all.some(c => (c.title ?? '').toLowerCase().includes(q))
    if (status === 'launched') { if (!launched?.has(g.root.id)) return false }
    else if (status !== 'all' && (status === 'published') !== isPublished(g)) return false
    if (languages.length) { if (!matchesLanguages(g, languages)) return false }
    else if (language !== 'all' && !g.all.some(c => c.language === language)) return false
    if (authors.length && !authors.includes(g.root.created_by)) return false
    return true
  })

  const by: Record<CourseSort, (a: CourseGroup, b: CourseGroup) => number> = {
    needs: (a, b) => lastEdited(b) - lastEdited(a),
    edited: (a, b) => lastEdited(b) - lastEdited(a),
    created: (a, b) => createdAt(b) - createdAt(a),
    az: (a, b) => title(a).localeCompare(title(b)),
    za: (a, b) => title(b).localeCompare(title(a)),
  }
  return kept.slice().sort(by[sort])
}
